import { useState, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  TextField,
  Select,
  ButtonGroup,
} from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import * as libphonenumber from 'libphonenumber-js';
import { isValidEmail } from '@hona-utils/email/isValidEmail';
import { parsedPhoneNumberFromString } from '@hona-utils/phone/parsePhoneNumber';
import CompanyLoginMessage from './CompanyLoginMessage/CompanyLoginMessage';
import { getCompanyLoginOptions } from '../../api/company';
import CompanyLogo from '../CompanyLogo';
import styles from './SignInPageML.module.css';
import PoweredByHona from '../../assets/powered-by-hona.svg';
import honaLogo from '../../assets/hona-standard.svg';
import {
  useSendMagicLink,
  useGetLoginIdentityFromAuthToken,
} from '../../hooks/signin.hooks';
import { setCookie } from '../../util/cookie';
import PreviewMode from '../AccountML/PreviewMode';

interface Props {
  startingErrorMessage: string | null;
}

function SignInPageML(props: Props) {
  const { startingErrorMessage } = props;
  const [loginPhone, setLoginPhone] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [hasSubmittedMagicLink, setHasSubmittedMagicLink] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    startingErrorMessage || '',
  );
  const time = new Date().getTime() / 1000;
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerStartDate, setTimerStartDate] = useState<Date | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>(honaLogo);
  const [loginMessage, setLoginMessage] = useState<string | null>(null);
  const subdomain = window.location.host.split('.')[1]
    ? window.location.host.split('.')[0]
    : false;
  const domain = window.location.host.split('.')[1];
  const path = useLocation().pathname;

  const [successMessage, setSuccessMessage] = useState<string>();
  const [currentCountryCode, setCurrentCountryCode] =
    useState<libphonenumber.CountryCallingCode>(
      libphonenumber.getCountryCallingCode('US'),
    );
  const [currentCountry, setCurrentCountry] =
    useState<libphonenumber.CountryCode>('US');
  const [loginIdentityType, setLoginIdentityType] = useState<
    'email' | 'phone' | null
  >(null);
  const [URLSearchParams] = useSearchParams();
  const authToken = URLSearchParams.get('c');
  const {
    data: defaultLoginIdentity,
    isError: isErrorGettingLoginIdentityFromToken,
    isSuccess: isSuccessGettingLoginIdentityFromToken,
    isLoading: isLoadingGettingLoginIdentityFromToken,
  } = useGetLoginIdentityFromAuthToken(authToken);

  useEffect(() => {
    if (timerSeconds > 0 && timerStartDate) {
      setTimeout(
        () =>
          setTimerSeconds(
            30 - (new Date().getTime() - timerStartDate.getTime()) / 1000,
          ),
        1000,
      );
    }
    if (timerSeconds <= 0 && timerStartDate) {
      setTimerStartDate(null);
      setSuccessMessage('');
    }
  }, [time]);

  useEffect(() => {
    async function setCompanySignInData(companySubdomain: string) {
      try {
        const companyLoginOptions =
          await getCompanyLoginOptions(companySubdomain);
        setCookie('session-type', 'magic-link');

        if (companyLoginOptions?.logoUrl) {
          setLogoUrl(companyLoginOptions.logoUrl);
        } else setLogoUrl(honaLogo);

        if (companyLoginOptions?.loginMessage?.trim())
          setLoginMessage(companyLoginOptions.loginMessage);
      } catch (err) {
        setLogoUrl(honaLogo);
        setLoginMessage(null);
      }
    }

    if (subdomain && subdomain !== 'www' && domain !== 'cloudfront') {
      setCompanySignInData(subdomain);
    } else {
      setLogoUrl(honaLogo);
    }
  }, [subdomain]);

  const getLoginIdentity = () => {
    if (loginIdentityType === 'email') {
      return loginEmail;
    }

    return parsedPhoneNumberFromString(
      loginPhone,
      currentCountry,
      'INTERNATIONAL',
    ); // Space number format +1 719 553 9507
  };
  const setLoginIdentity = (loginIdentity: string, type: 'email' | 'phone') => {
    if (type === 'email') {
      setLoginEmail(loginIdentity);
    } else {
      const parsedPhoneNumber = parsedPhoneNumberFromString(
        loginIdentity,
        currentCountry,
        'INTERNATIONAL',
      ); // Space number format +1 719 553 9507
      setLoginPhone(parsedPhoneNumber);
    }
  };

  const { mutate: sendMagicLinkMutation, isLoading } = useSendMagicLink({
    onSuccess: () => {
      setHasSubmittedMagicLink(true);
      setTimerStartDate(new Date());
      setTimerSeconds(30);
      setSuccessMessage(
        `Sign in link sent to ${getLoginIdentity()}. Please wait 30 seconds before resending the link.`,
      );

      setErrorMessage('');
    },
    onError: (error) => {
      let message =
        'Error to sending link. Please try again. If you continue to see this error please contact support@hona.com.';

      if (error.status >= 400 && error.status < 500) {
        message +=
          'Error to sending link. Please try again with a different phone number or email address.';
      }
      setSuccessMessage('');
      setErrorMessage(message);
      setHasSubmittedMagicLink(false);
      console.error(error);
    },
  });

  const requestMagicLink = async () => {
    if (
      loginIdentityType === 'phone' &&
      (!loginPhone ||
        !(libphonenumber.isValidNumber(loginPhone, currentCountry) as boolean))
    ) {
      setErrorMessage('Please enter a valid phone number.');
      setSuccessMessage('');
      setHasSubmittedMagicLink(false);
      return;
    }
    if (loginIdentityType === 'email' && !isValidEmail(loginEmail)) {
      setErrorMessage('Please enter a valid email address.');
      setSuccessMessage('');
      setHasSubmittedMagicLink(false);
      return;
    }

    const loginIdentity =
      loginIdentityType === 'phone'
        ? parsedPhoneNumberFromString(loginPhone, currentCountry, 'E.164') // No spaces number +17195539507
        : loginEmail;

    if (loginIdentityType) {
      sendMagicLinkMutation({
        loginIdentity,
        loginIdentityType,
        companySubdomain: subdomain || 'hona',
        path: path || '/',
      });
      return;
    }

    setErrorMessage(
      'Sign In link failed to send. Please try again. If you continue to see this error please contact support@hona.com.',
    );
    setSuccessMessage('');
  };
  if (
    isSuccessGettingLoginIdentityFromToken &&
    loginIdentityType === null &&
    !isLoadingGettingLoginIdentityFromToken
  ) {
    setLoginIdentityType(defaultLoginIdentity?.loginIdentityType || 'phone');
    if (defaultLoginIdentity?.loginIdentityType === 'phone') {
      const phoneNumber = libphonenumber.parsePhoneNumberFromString(
        defaultLoginIdentity?.loginIdentity as string,
      );

      setCurrentCountryCode(phoneNumber?.countryCallingCode || '1');
      setCurrentCountry(phoneNumber?.country || 'US');
    }
    setLoginIdentity(defaultLoginIdentity?.loginIdentity as string, 'email');
  }

  if (
    isErrorGettingLoginIdentityFromToken &&
    loginIdentityType === null &&
    !isLoadingGettingLoginIdentityFromToken
  ) {
    setLoginIdentityType('phone');
  }

  const countries = libphonenumber.getCountries();
  const countryCodes = countries.map((country) => ({
    country,
    callingCode: libphonenumber.getCountryCallingCode(country),
  }));
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.logo}>
          <CompanyLogo logoUrl={logoUrl} />
        </div>
        <div className={styles.text}>
          <h1>
            {!hasSubmittedMagicLink ? 'Sign In Via Link' : 'Sign In Link Sent!'}
          </h1>
          <p>
            {!hasSubmittedMagicLink
              ? 'Please Enter an email address or a phone number to send the sign in link.'
              : 'Please check your email or phone for the  sign in link.'}
          </p>
        </div>
      </div>

      <div className={styles.login}>
        <div className={styles['login-type']}>
          <ButtonGroup aria-label="outlined primary button group">
            <Button
              color="secondary"
              variant={loginIdentityType !== 'email' ? 'contained' : 'outlined'}
              onClick={() => setLoginIdentityType('phone')}
            >
              Phone
            </Button>
            <Button
              color="secondary"
              variant={loginIdentityType === 'email' ? 'contained' : 'outlined'}
              onClick={() => setLoginIdentityType('email')}
            >
              Email
            </Button>
          </ButtonGroup>
        </div>
        {loginIdentityType === 'email' && (
          <TextField
            id="loginIdentityEmail"
            label="Email"
            fullWidth
            value={loginEmail}
            onChange={(event) => setLoginIdentity(event.target.value, 'email')}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                requestMagicLink();
              }
            }}
            sx={{ mb: '1em' }}
            className={styles['text-input']}
          />
        )}
        {(!loginIdentityType || loginIdentityType === 'phone') && (
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <Select
              native
              fullWidth
              value={`${currentCountryCode}-${currentCountry}`}
              onChange={(event) => {
                const callingCode = event.target.value.split(
                  '-',
                )[0] as libphonenumber.CountryCallingCode;
                const country = event.target.value.split(
                  '-',
                )[1] as libphonenumber.CountryCode;
                setCurrentCountryCode(callingCode);
                setCurrentCountry(country);
              }}
              sx={{ mb: '1em', width: 190 }}
            >
              {countryCodes.map((country) => (
                <option
                  key={country.country}
                  value={`${country.callingCode}-${country.country}`}
                >
                  {`+${country.callingCode} (${country.country})`}
                </option>
              ))}
            </Select>
            <TextField
              id="loginIdentityPhone"
              label="Phone Number"
              fullWidth
              type="tel"
              value={loginPhone}
              onChange={(event) =>
                setLoginIdentity(event.target.value, 'phone')
              }
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  requestMagicLink();
                }
              }}
              sx={{ mb: '1em' }}
              className={styles['text-input']}
            />
          </div>
        )}

        <Button
          disabled={isLoading || timerSeconds > 0}
          color="secondary"
          onClick={requestMagicLink}
          variant="contained"
          fullWidth
          startIcon={
            !isLoading ? undefined : <CircularProgress size={20} color="info" />
          }
        >
          {!hasSubmittedMagicLink && 'Send Link'}
          {hasSubmittedMagicLink && timerSeconds <= 0 && `Resend Link`}
          {hasSubmittedMagicLink && timerSeconds > 0 && (
            <CircularProgress
              variant="determinate"
              color="secondary"
              size={20}
              value={(timerSeconds / 30) * 100}
            />
          )}
        </Button>
        {errorMessage && (
          <div className={styles.error}>
            <p>{errorMessage}</p>
          </div>
        )}

        {successMessage && (
          <div className={styles.success}>
            <p>{successMessage}</p>
          </div>
        )}
        <CompanyLoginMessage loginMessage={loginMessage} />
      </div>

      <div className={styles.footer}>
        <div className={styles['powered-by-milestones']}>
          <img width={150} src={PoweredByHona} alt="Powered by Hona" />
        </div>
      </div>
      <div className={styles.preview}>
        <PreviewMode sx={{ opacity: 0.4 }} variant="text" text="Demo" />
      </div>
    </div>
  );
}

export default SignInPageML;
